import React, { useContext, useEffect, useState } from 'react'
import BigModal from '../../../components/Modals/BigModal'
import moment from 'moment'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import API from '../../../services/API'
import MFALogin from '../../../helpers/auth/MFALogin'
import AnDateField from '../../../components/DateTime/DateField'
import { Book, PersonAddAlt1, Search } from '@mui/icons-material'
import CustomDataGrid from '../../../components/DataGrid/CustomDataGrid'
import { BookingContext } from './NewBooking'
import Loading, { LoadingSmall } from '../../loading/loadingPage'
import { GlobalContext, TenantSettingsContext } from '../../../App'
import { regionLabels } from '../../../helpers/region'
import OrganisationSelector from './OrganisationSelector'

const PatientLookup = ({
  open,
  onClose,
  setSelectedPatient,
  embedded,
  createNewPT,
  onRowClick,
  portalMode = false,
  portalOrganisation = null,
}) => {
  const [loading, setLoading] = useState(true)
  const [requiresMFA, setRequiresMFA] = useState(true)
  const blankSearch = {
    lastName: '',
    firstName: '',
    gender: null,
    dob: null,
    postCode: '',
    nhsNumber: '',
    patientID: '',
  }
  const [searchForm, setSearchForm] = useState(blankSearch)
  const [insufficientData, setInsufficientData] = useState(false)
  const [searchResults, setSearchResults] = useState(null)
  const [searchError, setSearchError] = useState(false)
  const { tenantInfo } = useContext(GlobalContext)
  const { orgSettings } = useContext(TenantSettingsContext).orgConfig || {}
  const tenantName =
    orgSettings?.orgShortName || orgSettings?.orgName || 'AmbuNet'
  const labels = regionLabels(tenantInfo?.region)
  const { setFormData } = useContext(BookingContext)
  const [orgOptions, setOrgOptions] = useState([])

  useEffect(() => {
    if (portalMode) return
    // TODO: Make this (and all other lookups) search after 2 letters instead of just pulling them all
    API.get('/pts/organisationoptions').then((res) => {
      if (res.status === 200) {
        setOrgOptions(res.data)
      }
    })
  }, [portalMode])

  useEffect(() => {
    if (insufficientData) {
      setInsufficientData(false)
    }
    if (searchResults) {
      setSearchResults(null)
    }
  }, [searchForm])

  useEffect(() => {
    setSearchForm(blankSearch)
  }, [open])

  useEffect(() => {
    if (portalMode) {
      setRequiresMFA(false)
      setLoading(false)
      return
    }

    API.get('/pts/checkmfa')
      .then((res) => {
        if (res.status === 200) {
          setRequiresMFA(false)
        }
      })
      .finally(() => setLoading(false))
  }, [portalMode])

  const handleSearch = () => {
    const {
      nhsNumber,
      firstName,
      lastName,
      dob,
      postCode,
      patientID,
      hospitalNumber,
    } = searchForm || {}

    const sufficientData =
      portalMode
        ? (!!(nhsNumber && nhsNumber.length === 10) &&
            (!!lastName || !!moment(dob).isValid())) ||
          (!!lastName && !!moment(dob).isValid() && !!postCode) ||
          // Portal lookups are already scoped to the user's own hospital, so
          // its hospital number alone is enough to identify the patient
          !!hospitalNumber?.mrn
        : !!patientID ||
          !!(nhsNumber && nhsNumber.length === 10) ||
          (!!lastName && !!moment(dob).isValid() && !!postCode) ||
          (!!firstName && !!lastName && !!moment(dob).isValid()) ||
          (hospitalNumber?.mrn && hospitalNumber?.organisation)

    if (!sufficientData) {
      setInsufficientData(true)
    } else {
      API.post(portalMode ? '/pts/portal/patient-lookup' : '/pts/patient-lookup', {
        ...searchForm,
        hospitalNumber: {
          ...searchForm.hospitalNumber,
          organisation:
            searchForm.hospitalNumber?.organisation ||
            portalOrganisation?._id ||
            portalOrganisation ||
            null,
        },
      })
        .then((res) => {
          if (res.status === 200) {
            // setSearchResults(
            //   res.data.map((x) => ({
            //     id: x._id,
            //     ...x.demographics,
            //   }))
            // )
            setSearchResults(res.data)
          } else {
            // API never rejects on 4xx/5xx — without this an auth failure
            // (e.g. not a PTS Co-Ordinator) silently renders as zero results
            setSearchError(true)
          }
        })
        .catch(() => setSearchError(true))
    }
  }

  return (
    <Stack gap={1}>
      <Typography variant="title2">Patient Lookup</Typography>
      {loading ? (
        <LoadingSmall />
      ) : requiresMFA ? (
        <Box>
          <MFALogin onSuccess={() => setRequiresMFA(false)} />
        </Box>
      ) : (
        <Grid
          container
          spacing={2}
          component="form"
          onSubmit={(e) => {
            e.preventDefault()
            handleSearch()
          }}
        >
          {tenantInfo?.isDemo && (
            // Demo-tenant guide: Margaret Ashworth is seeded by
            // scripts/demo-seed/modules/08-pts.js (MRN = 'KH' +
            // patientID.slice(1, 7)) and reseeded nightly, so these values
            // are stable.
            <Grid item xs={12}>
              <Alert severity="info">
                <b>Demo data:</b> try searching {labels.hospitalNumberLabel}{' '}
                <b>KHKX7M2R</b>
                {portalMode
                  ? ' on its own'
                  : ' with Kings Halton General Hospital selected as the hospital'}
                , or surname <b>Ashworth</b> with date of birth{' '}
                <b>05/03/1942</b> and postcode <b>B46 2DL</b> to find Margaret
                Ashworth, one of the demo patients.
              </Alert>
            </Grid>
          )}
          <Grid item xs={12} md={6} lg={6}>
            <TextField
              fullWidth
              label="Surname"
              value={searchForm.lastName}
              autoComplete="none"
              onChange={(e) =>
                setSearchForm((prev) => ({
                  ...prev,
                  lastName: e.target.value,
                }))
              }
            />
          </Grid>
          <Grid item xs={12} md={6} lg={6}>
            <TextField
              fullWidth
              label="First Name"
              value={searchForm.firstName}
              autoComplete="none"
              onChange={(e) =>
                setSearchForm((prev) => ({
                  ...prev,
                  firstName: e.target.value,
                }))
              }
            />
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            <AnDateField
              date={searchForm.dob}
              onChange={(dob) => setSearchForm((prev) => ({ ...prev, dob }))}
              label="DOB"
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            <TextField
              value={searchForm.postCode}
              onChange={(e) =>
                setSearchForm((prev) => ({
                  ...prev,
                  postCode: e.target.value,
                }))
              }
              label="Post Code"
              fullWidth
              autoComplete="none"
            />
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            <Autocomplete
              options={['Male', 'Female', 'Non-Binary']}
              value={searchForm.gender}
              onChange={(e, gender) =>
                setSearchForm((prev) => ({ ...prev, gender: gender || null }))
              }
              renderInput={(params) => <TextField {...params} label="Gender" />}
            />
          </Grid>

          <Grid item xs={12} md={6} lg={6}>
            <TextField
              label={`${tenantName} Patient ID`}
              autoComplete="off"
              value={searchForm.patientID || ''}
              disabled={portalMode}
              helperText={
                portalMode
                  ? 'Internal patient IDs are not available to hospital users'
                  : 'Searches on its own - no other fields needed'
              }
              onChange={(e) => {
                // Patient IDs are alphanumeric (e.g. PKX7M2R4A) — the old
                // digits-only filter silently emptied the field
                const cleaned = e.target.value
                  .replace(/[^a-zA-Z0-9]/g, '')
                  .toUpperCase()
                  .slice(0, 12)

                setSearchForm((prev) => ({
                  ...prev,
                  patientID: cleaned,
                }))
              }}
              inputProps={{
                maxLength: 12,
              }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={12} lg={6}>
            <TextField
              label={labels.nationalIdLabel}
              autoComplete="off"
              value={
                searchForm.nhsNumber
                  ? searchForm.nhsNumber.replace(
                      /(\d{3})(\d{3})(\d{0,4})/,
                      (_, a, b, c) => (c ? `${a} ${b} ${c}` : `${a} ${b}`),
                    )
                  : ''
              }
              onChange={(e) => {
                const digitsOnly = e.target.value
                  .replace(/\D/g, '')
                  .slice(0, 10)

                setSearchForm((prev) => ({
                  ...prev,
                  nhsNumber: digitsOnly,
                }))
              }}
              inputProps={{
                inputMode: 'numeric',
                maxLength: 12, // includes spaces
              }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 3,
                backgroundColor: 'background.default',
              }}
            >
              <Stack
                direction={{ xs: 'column', lg: 'row' }}
                alignItems="center"
                spacing={2}
              >
                <Typography variant="body2" color="text.secondary">
                  Search by {labels.hospitalNumberNoun} Number
                </Typography>
                <TextField
                  label={labels.hospitalNumberLabel}
                  onChange={(e) =>
                    setSearchForm((prev) => ({
                      ...prev,
                      hospitalNumber: {
                        ...prev.hospitalNumber,
                        mrn: e.target.value,
                      },
                    }))
                  }
                  sx={{ flex: 0.8 }}
                  autoComplete="off"
                />
                {portalMode ? (
                  <TextField
                    label="Hospital"
                    value={portalOrganisation?.orgName || 'Your hospital'}
                    sx={{ flex: 1 }}
                    fullWidth
                    disabled
                  />
                ) : (
                  <OrganisationSelector
                    onChange={(newValue) =>
                      setSearchForm((prev) => ({
                        ...prev,
                        hospitalNumber: {
                          ...prev.hospitalNumber,
                          organisation: newValue?._id || newValue,
                        },
                      }))
                    }
                    fullWidth
                    options={orgOptions}
                    showAddButton={false}
                    sx={{ flex: 1 }}
                  />
                )}
              </Stack>
            </Paper>
          </Grid>
          {insufficientData && (
            <Grid item xs={12}>
              <Alert severity="error">
                {portalMode ? (
                  <>
                    Please provide your hospital's {labels.hospitalNumberLabel}
                    , an {labels.nationalIdLabel} with surname or date of
                    birth, or surname, date of birth, and postcode.
                  </>
                ) : (
                  <>
                    Please provide an {labels.nationalIdLabel} with surname or
                    date of birth, a hospital/MRN number with surname or date
                    of birth, or surname, date of birth, and postcode. <br />
                    If searching by {labels.hospitalNumberLabel}, please ensure
                    you've entered a number <b>and</b> selected a hospital.
                  </>
                )}
              </Alert>
            </Grid>
          )}
          <Grid item xs={12}>
            <Button
              fullWidth
              variant="contained"
              endIcon={<Search />}
              type="submit"
            >
              Search
            </Button>
          </Grid>
          {searchResults?.length > 0 ? (
            <Grid item xs={12}>
              <CustomDataGrid
                rows={searchResults}
                columns={[
                  {
                    field: 'patientID',
                    headerName: 'Patient ID',
                    minWidth: 80,
                    flex: 0.4,
                  },
                  {
                    field: 'lastName',
                    headerName: 'Surname',
                    minWidth: 100,
                    flex: 0.4,
                    valueGetter: (value, row) => row.demographics.lastName,
                  },
                  {
                    field: 'firstName',
                    headerName: 'First Name',
                    minWidth: 100,
                    flex: 0.4,
                    valueGetter: (value, row) => row.demographics.firstName,
                  },
                  {
                    field: 'dob',
                    headerName: 'Date of Birth',
                    minWidth: 120,
                    flex: 0.4,
                    renderCell: ({ value }) =>
                      value && moment(value).format('DD/MM/YYYY'),
                    valueGetter: (value, row) => row.demographics.dob,
                  },
                  {
                    field: 'nhsNumber',
                    headerName: labels.nationalIdLabel,
                    minWidth: 140,
                    flex: 0.7,
                    valueGetter: (value, row) => row.demographics.nhsNumber,
                  },
                  {
                    field: 'address',
                    headerName: 'Address',
                    valueGetter: (value, row) => row.demographics.postCode,
                    renderCell: ({ row }) => {
                      const addressFields = [
                        row.demographics.addressLine1,
                        row.demographics.addressLine2,
                        row.demographics.town,
                        row.demographics.city,
                        row.demographics.postCode,
                      ].filter(Boolean)
                      return addressFields.join(', ')
                    },
                    flex: 1,
                    minWidth: 200,
                  },
                  {
                    field: 'gender',
                    headerName: 'Gender',
                    minWidth: 80,
                    flex: 0.4,
                    valueGetter: (value, row) => row.demographics.gender,
                  },
                  {
                    field: 'gpSurgery',
                    headerName: 'GP Surgery',
                    minWidth: 120,
                    flex: 0.6,
                    valueGetter: (value, row) => row.demographics.gpSurgery,
                  },
                  // TODO: HOSPITAL NUMBER
                ]}
                getRowId={(row) => row._id}
                disableRowSelectionOnClick
                onRowClick={({ row }) => {
                  if (onRowClick) {
                    onRowClick(row)
                    return
                  }
                  setSelectedPatient(row)
                  setFormData((prev) => ({
                    ...prev,
                    patient: row._id,
                  }))
                  onClose()
                }}
                slots={{
                  toolbar: () => null,
                  footer: () => null,
                }}
              />
            </Grid>
          ) : (
            searchResults && (
              <Grid item xs={12}>
                <Chip
                  sx={{ width: '100%', borderRadius: 1 }}
                  color="anPurple"
                  label="No Matching Records"
                />
              </Grid>
            )
          )}
          <Grid item xs={12}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<PersonAddAlt1 />}
              color="contrast"
              onClick={() => createNewPT(true)}
            >
              Create a New Patient Record
            </Button>
          </Grid>
        </Grid>
      )}
    </Stack>
  )
}

export default PatientLookup
